import { Loader2 } from "lucide-react";
import { forwardRef } from "react";

import { cn } from "@/lib/utils";

const VARIANTS = {
  primary: "bg-primary text-primary-foreground hover:brightness-110 shadow-[0_8px_24px_-12px_hsl(var(--primary)/0.6)]",
  secondary: "bg-secondary text-foreground border border-input hover:border-primary/60",
  ghost: "bg-transparent text-foreground hover:bg-card",
  danger: "bg-destructive text-destructive-foreground hover:brightness-110",
};

export const Button = forwardRef(function Button(
  { title, icon: Icon, variant = "primary", loading, disabled, compact, className, children, ...p },
  ref,
) {
  return (
    <button ref={ref} type="button" disabled={disabled || loading} aria-busy={loading || undefined}
      className={cn(
        "focus-ring inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200",
        "active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 disabled:active:scale-100",
        compact ? "min-h-11 px-4 text-sm" : "min-h-14 px-6 text-base",
        VARIANTS[variant], className,
      )} {...p}>
      {loading ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> : Icon ? <Icon className="h-5 w-5 shrink-0" aria-hidden /> : null}
      {title ?? children}
    </button>
  );
});

export function IconButton({ icon: Icon, label, className, tint, ...p }) {
  return (
    <button type="button" aria-label={label} title={label}
      className={cn("focus-ring inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors",
        "hover:bg-card disabled:cursor-not-allowed disabled:opacity-30", tint ?? "text-foreground", className)} {...p}>
      <Icon className="h-5 w-5" aria-hidden />
    </button>
  );
}

export function Field({ id, label, error, multiline, className, inputClassName, ...p }) {
  const Comp = multiline ? "textarea" : "input";
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <label htmlFor={id} className="text-sm font-medium text-secondary-foreground">{label}</label>
      <Comp id={id} aria-invalid={!!error || undefined}
        className={cn("w-full rounded-xl border border-border bg-card px-4 text-base text-foreground placeholder:text-muted-foreground",
          "outline-none transition-colors focus:border-primary focus-visible:ring-2 focus-visible:ring-ring/40",
          multiline ? "min-h-[160px] resize-y py-3 leading-relaxed" : "h-[52px]", error && "border-destructive", inputClassName)} {...p} />
      {error ? <p className="text-[13px] text-destructive" data-testid={p["data-testid"] ? `${p["data-testid"]}-error` : undefined}>{error}</p> : null}
    </div>
  );
}

const PILL = {
  neutral: "bg-secondary text-secondary-foreground",
  success: "bg-success text-white",
  error: "bg-destructive text-white",
  info: "bg-info text-white",
  brand: "bg-primary/15 text-primary",
};
export function Pill({ label, kind = "neutral", className, ...p }) {
  return (
    <span className={cn("inline-flex items-center rounded-full px-3 py-1 text-xs font-medium shadow-sm", PILL[kind], className)} {...p}>
      {label}
    </span>
  );
}

export function Loading({ label, className }) {
  return (
    <div className={cn("flex flex-1 flex-col items-center justify-center gap-3 py-24", className)} data-testid="loading-indicator" role="status">
      <Loader2 className="h-9 w-9 animate-spin text-primary" aria-hidden />
      {label ? <p className="text-[13px] text-muted-foreground">{label}</p> : <span className="sr-only">Loading</span>}
    </div>
  );
}

export function ProgressBar({ value, className, ...p }) {
  return (
    <div className={cn("h-1.5 overflow-hidden rounded-full bg-secondary", className)} role="progressbar"
      aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value)}>
      <div className="h-full rounded-full bg-primary transition-[width] duration-300" style={{ width: `${value}%` }} {...p} />
    </div>
  );
}
